import { colors } from './colors';
import { STATUS_MODEL } from './statusEnum';

/**
 * Special display states for the identity-validation ProcessCard in the
 * external registration flow. These four cases need their own colors and
 * legends, different from the generic status maps, so they live here instead
 * of polluting statusColorEnum / statusEnum.
 *
 *  - VALIDATED            -> identity already validated (status: approved)        -> green
 *  - IN_REVIEW            -> uploaded, under manual review (status: pending to approve) -> blue
 *  - VALIDATION_OPTIONAL  -> validation flag off, not validated (status: pending to upload) -> grey
 *  - VALIDATION_REQUIRED  -> validation flag on, not validated  (status: pending to upload) -> warning
 */
export const IDENTITY_VALIDATION_CARD = {
    VALIDATED: 'VALIDATED',
    IN_REVIEW: 'IN_REVIEW',
    VALIDATION_OPTIONAL: 'VALIDATION_OPTIONAL',
    VALIDATION_REQUIRED: 'VALIDATION_REQUIRED',
};

export const identityValidationCardConfig = {
    [IDENTITY_VALIDATION_CARD.VALIDATED]: {
        background: colors.success100,
        fontColor: colors.success700,
        borderColor: colors.success150,
        legend: 'VALIDATED',
    },
    [IDENTITY_VALIDATION_CARD.IN_REVIEW]: {
        background: colors.secondary100,
        fontColor: colors.secondary700,
        borderColor: colors.secondary150,
        legend: 'in_review',
    },
    [IDENTITY_VALIDATION_CARD.VALIDATION_OPTIONAL]: {
        background: colors.shades200,
        fontColor: colors.shades700,
        borderColor: colors.shades150,
        legend: 'validation_optional',
    },
    [IDENTITY_VALIDATION_CARD.VALIDATION_REQUIRED]: {
        background: colors.warning100,
        fontColor: colors.warning700,
        borderColor: colors.warning150,
        legend: 'validation_required',
    },
};

const IN_REVIEW_STATUSES = [
    STATUS_MODEL.PENDING_TO_APPROVE, // 110
    STATUS_MODEL.PENDING_REVIEW,     // 133
];

const PENDING_TO_UPLOAD_STATUSES = [
    STATUS_MODEL.PENDING_TO_UPLOAD,             // null
    STATUS_MODEL.PENDING_TO_UPLOAD_INFORMATION, // 111
];

/**
 * Resolves which of the four special identity-validation card types applies.
 * Returns null when the status is not one of these special cases so the caller
 * can fall back to the generic status handling.
 *
 * @param {number|null} identityValidation - invitation.identityValidation status id
 * @param {boolean} validationVisitor - userRestriction.validationVisitor flag
 * @returns {string|null} one of IDENTITY_VALIDATION_CARD or null
 */
export const getIdentityValidationCardType = (identityValidation, validationVisitor) => {
    if (identityValidation === STATUS_MODEL.VALIDATED) {
        return IDENTITY_VALIDATION_CARD.VALIDATED;
    }
    if (IN_REVIEW_STATUSES.includes(identityValidation)) {
        return IDENTITY_VALIDATION_CARD.IN_REVIEW;
    }
    if (PENDING_TO_UPLOAD_STATUSES.includes(identityValidation)) {
        return validationVisitor === false
            ? IDENTITY_VALIDATION_CARD.VALIDATION_OPTIONAL
            : IDENTITY_VALIDATION_CARD.VALIDATION_REQUIRED;
    }
    return null;
};

/**
 * Returns the display config ({ background, fontColor, borderColor, legend })
 * for the identity-validation card, or null when no special case applies.
 *
 * @param {number|null} identityValidation - invitation.identityValidation status id
 * @param {boolean} validationVisitor - userRestriction.validationVisitor flag
 * @returns {{background: string, fontColor: string, borderColor: string, legend: string}|null}
 */
export const getIdentityValidationCard = (identityValidation, validationVisitor) => {
    const type = getIdentityValidationCardType(identityValidation, validationVisitor);
    return type ? identityValidationCardConfig[type] : null;
};
